<template>
  <div class="row">
    <div v-if="submerchant" class="col-md-12">
      <div class="row">
        <div class="col-md-12">
          <submerchant-navigation :submerchant-id="submerchantId"></submerchant-navigation>
        </div>
      </div>

      <div class="row">
        <div class="col-12 col-md-8">
          <div class="card">
            <h2 class="card-header">Driver overview</h2>
            <div class="card-body">
              <h5 class="card-title">
                {{ submerchant.name }}
                <span v-if="submerchant.reference">
                  <code>{{ submerchant.reference }}</code>
                </span>
              </h5>
              <h6 class="card-subtitle mb-4 text-muted">
                Driver ID:
                <code>{{ submerchant.id }}</code>
              </h6>

              <div class="row">
                <div class="col-12 col-md-6">
                  <table class="table">
                    <tbody>
                      <tr>
                        <th>Address</th>
                        <td>
                          {{ submerchant.addressLine1 }}
                          <br />
                          {{ submerchant.addressLine2 }}
                          <br />
                          {{ submerchant.addressLine3 }}
                          <br />
                          {{ submerchant.addressCity }}
                          <br />
                          {{ submerchant.regionCode }},
                          {{ submerchant.countryCode }}
                          <br />
                        </td>
                      </tr>
                      <tr>
                        <th>Postcode</th>
                        <td>{{ submerchant.postcode }}</td>
                      </tr>
                      <tr>
                        <th>Phone number</th>
                        <td>{{ submerchant.phoneNumber }}</td>
                      </tr>
                      <tr>
                        <th>Email address</th>
                        <td>{{ submerchant.emailAddress }}</td>
                      </tr>
                      <tr>
                        <th>Created</th>
                        <td>
                          {{ submerchant.createdAt | moment("from", "now") }}
                        </td>
                      </tr>
                      <tr>
                        <th>Last updated</th>
                        <td>
                          {{ submerchant.updatedAt | moment("from", "now") }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="d-none d-md-block col-md-6">
                  <div v-if="addressMap" class="mapcontainer" style="height:325px">
                    <l-map ref="map" :zoom="addressMap.zoom" :center="addressMap.center">
                      <l-tile-layer :url="addressMap.url" :attribution="addressMap.attribution"></l-tile-layer>
                      <l-marker :lat-lng="addressMap.marker"></l-marker>
                    </l-map>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-md-4 col-lg-4">
          <submerchant-terminals-list :submerchant-id="submerchantId"></submerchant-terminals-list>

        <!-- A card with information on the driver emergency kit and a download button -->
          <div class="card">
            <h2 class="card-header">Emergency kit</h2>
            <div class="card-body">
              <p class="card-text">
                Download this emergency kit so that your driver can take payments even without their CabCard terminal.
              </p>
              <p class="card-text">
                Your driver can also download it themselves on their <a href="https://my.cabcard.services/scan-to-pay">driver dashboard</a>.
              </p>
              <!-- download button -->
              <div class="text-center">
                <a class="btn btn-primary text-white" @click="pdfDownload">
                  <i class="fa fa-download mr-2"></i> Download Driver Emergency Kit
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <submerchant-sales-list :submerchant-id="submerchantId"></submerchant-sales-list>
        </div>
      </div>
    </div>
    <div v-else>
      <div v-if="loading" class="spinner-container">
        <double-bounce size="60px"></double-bounce>
      </div>
    </div>
  </div>
</template>

<script>
import { DoubleBounce } from "vue-loading-spinner";
import { LMap, LTileLayer, LMarker } from "vue2-leaflet";
import SubmerchantSalesList from "./SubmerchantSalesList";
import SubmerchantTerminalsList from "./SubmerchantTerminalsList";
import SubmerchantNavigation from "./SubmerchantNavigation";
import pdfUtil from "@optimuspay/emergency-kit-utility";
import file from "@optimuspay/emergency-kit-utility/static/files/emergency-kit-template.pdf";
import font1 from "@optimuspay/emergency-kit-utility/static/fonts/HKGrotesk-ExtraBold.otf";
import font2 from "@optimuspay/emergency-kit-utility/static/fonts/HKGrotesk-Regular.otf";

export default {
  name: "SubmerchantPage",
  components: {
    DoubleBounce,
    LMap,
    LTileLayer,
    LMarker,
    SubmerchantSalesList,
    SubmerchantTerminalsList,
    SubmerchantNavigation
  },
  data() {
    return {
      error: null,
      loading: null,
      submerchant: null,
      addressMap: null
    };
  },
  props: ["submerchantId"],
  beforeMount() {
    if (this.submerchantId === "performance") {
      // not a submerchant - redirect to performance page
      this.$router.push({ name: "Submerchants Performance" });
    }
    this.loading = true;
    this.axios
      .get(`/v1/submerchants/${this.submerchantId}`)
      .then(response => {
        this.loading = false;
        this.submerchant = response.data;

        if (
          this.submerchant.addressLatitude &&
          this.submerchant.addressLongitude
        ) {
          // set map
          const latLng = L.latLng(
            this.submerchant.addressLatitude,
            this.submerchant.addressLongitude
          );

          this.addressMap = {
            zoom: 13,
            center: latLng,
            url: "https://{s}.tile.osm.org/{z}/{x}/{y}.png",
            attribution:
              '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
            marker: latLng
          };

          this.$nextTick(function () {
            this.$refs.map.mapObject.invalidateSize();
          });
        }
      })
      .catch(error => {
        this.loading = false;
        this.error = "Failed to load submerchant";
      });
  },
  methods: {
    pdfDownload() {
      pdfUtil.generate(this.submerchant, {template: file, fontHeader: font1, fontBody: font2})
        .then(pdf => {
          pdfUtil.download(pdf)
        })
    }
  }
};
</script>
